/**
 * studio/src/lib/list-agent-activity.ts
 *
 * Pure helpers backing the Agents-tab data path. Lives outside
 * `src/lib/appwrite.ts` so the studio's existing seam-of-mocking
 * pattern (see `get-pitch-video-by-id.ts`) covers the filter
 * semantics without an SDK mock.
 *
 * Why this exists (2026-07-01 agents-tab 500 regression, lesson):
 *
 * The previous `listAgentActivityForUser` in `appwrite.ts` issued
 * an `Query.or([Query.equal(metadata.userId), Query.equal(metadata.creatorId)])`
 * to surface both the new (post-2026-06-11) and legacy
 * (pre-2026-06-11) audit-row keys. Appwrite 1.9.5 self-hosted
 * returns `500 general_unknown` for that query against the
 * `audit_log` table — `metadata` is a stringified-JSON column,
 * not a typed JSON-path index. The studio's Agents tab rendered
 * "Server Error" verbatim because the wrapper had no try/catch
 * and the Vue component rendered the raw error message.
 *
 * The fix: stop trying to do this in Appwrite. The `audit_log`
 * table has `read("any")` and is bounded in size for an active
 * site (an agent does dozens of writes per session, not
 * millions), so a list-then-filter-in-JS read is cheap. This
 * module exposes the pure filter so it can be unit-tested
 * without the SDK.
 */
export interface AgentActivityRow {
  $id?: string;
  $createdAt?: string;
  /**
   * Appwrite's documented `createdAt` shape is the `$createdAt`
   * system column above. Some writers (notably the Studio's in-app
   * `logAudit` helper at `src/lib/appwrite.ts`) ALSO set a custom
   * `createdAt` column so the row's timestamp matches the local
   * clock at write time, not the Appwrite server's clock. Both
   * shapes are valid; the formatter and filter prefer `createdAt`
   * when present and fall back to `$createdAt`.
   */
  createdAt?: string;
  action?: string;
  targetType?: string;
  targetId?: string;
  /** The Appwrite users.$id of the actor (post-2026-06-11 rows). */
  actorUserId?: string | null;
  /**
   * Stringified JSON blob containing additional context. Pre-2026-06-11
   * rows populated `metadata.creatorId` (the creators.$id). Post-2026-06-11
   * rows populate `metadata.userId` (the Appwrite users.$id). New writes
   * (via `logAudit` in appwrite.ts) set BOTH the top-level `actorUserId`
   * AND a `metadata` blob — either key suffices for matching.
   *
   * The Studio's in-app `logAudit` helper writes via the browser SDK
   * (`data: { metadata: { ... } }`); the SDK returns the object shape,
   * not a string. The pure helpers in this module accept BOTH — the
   * string path covers the backend writers (pitch-write, pitch-transition,
   * pitch-rate, pitch-og-generate, etc.) and the object path covers
   * the Studio's own writes.
   */
  metadata?: string | Record<string, unknown> | null;
}

/**
 * Pure owner-matcher. Returns the rows that "belong" to the signed-in
 * creator — either via the top-level `actorUserId` column (post-migration
 * rows) or the legacy `metadata.creatorId` / `metadata.userId` keys inside
 * the stringified `metadata` blob.
 *
 * Order is preserved. Corrupt JSON blobs and rows with no matching key
 * are silently dropped (the table is `read("any")` so the SDK returns
 * everyone's rows; we filter down to the caller's slice here).
 *
 * @param rows   Audit-log rows from Appwrite's listDocuments call.
 * @param userId     The Appwrite users.$id of the signed-in creator.
 * @param creatorId  The Appwrite creators.$id of the signed-in creator
 *                   (used only for the pre-2026-06-11 `metadata.creatorId`
 *                   fallback match).
 */
export function filterAgentActivityForUser(
  rows: AgentActivityRow[],
  userId: string,
  creatorId: string,
): AgentActivityRow[] {
  const out: AgentActivityRow[] = [];
  for (const row of rows) {
    if (matchesAgentActivityRow(row, userId, creatorId)) {
      out.push(row);
    }
  }
  return out;
}

/**
 * Row-level match predicate. Exported for the rare caller that
 * wants to test one row in isolation.
 */
export function matchesAgentActivityRow(
  row: AgentActivityRow,
  userId: string,
  creatorId: string,
): boolean {
  // Fast path: post-migration rows have actorUserId set directly.
  // `actorUserId` may be stored as empty string rather than null on
  // some legacy writes — treat empty string as "not set".
  if (typeof row.actorUserId === "string" && row.actorUserId.length > 0) {
    return row.actorUserId === userId;
  }

  // Slow path: parse the metadata blob. We tolerate every failure
  // mode by returning false (the row simply doesn't match this
  // user). A corrupt blob does NOT throw — Appwrite returned the
  // row successfully, so its owner-matching shouldn't blow up.
  const raw = row.metadata;
  // The metadata column can arrive as a string (backend writers) or
  // as an already-parsed object (Studio's in-app `logAudit` writes
  // through the browser SDK). Both must match.
  let meta: Record<string, unknown> | null = null;
  if (typeof raw === "string") {
    if (raw.length === 0) return false;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return false;
    }
    meta = parsed as Record<string, unknown>;
  } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    meta = raw as Record<string, unknown>;
  } else {
    return false;
  }
  if (meta.userId === userId) return true;
  if (meta.creatorId === creatorId) return true;
  return false;
}

/**
 * Activity view filter tabs (added 2026-07-06 with the rename to
 * `/activity`). The "all" tab is a no-op (returns the input). The
 * "agents" tab surfaces only rows attributed to an SDK / MCP
 * client — either the new `metadata.actorChannel === "agent"`
 * tag (post-2026-07-06 rows) or the legacy `key.*` action prefix
 * (every pre-rename row that touched an API key). The "pitches"
 * tab hides ratings, bunny sweeps, and Stripe noise so the
 * pitcher can review just the pitch lifecycle.
 *
 * The function is a pure helper so the view's filter behavior
 * is testable without instantiating the Appwrite SDK.
 */
export type ActivityFilter = "all" | "agents" | "pitches";

const KEY_ACTION_PREFIX = "key.";

function metadataActorChannel(meta: unknown): string | undefined {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return undefined;
  const channel = (meta as Record<string, unknown>).actorChannel;
  return typeof channel === "string" ? channel : undefined;
}

function readMeta(row: AgentActivityRow): Record<string, unknown> | null {
  const raw = row.metadata;
  if (typeof raw === "string") {
    if (raw.length === 0) return null;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

export function filterActivityByTab(
  rows: AgentActivityRow[],
  filter: ActivityFilter,
): AgentActivityRow[] {
  if (filter === "all") return rows.slice();
  const out: AgentActivityRow[] = [];
  for (const row of rows) {
    const action = String(row.action ?? "");
    const meta = readMeta(row);
    const channel = metadataActorChannel(meta);
    if (filter === "agents") {
      if (channel === "agent") {
        out.push(row);
        continue;
      }
      // Legacy fallback: every `key.*` action came through the
      // pitcher-mcp / SDK even before the channel tag existed.
      if (action.startsWith(KEY_ACTION_PREFIX)) {
        out.push(row);
      }
      continue;
    }
    if (filter === "pitches") {
      if (action.startsWith("pitch.") || action === "og.generated") {
        out.push(row);
      }
    }
  }
  return out;
}
